import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const workspaceKeys = {
  all: ['workspaces'] as const,
  detail: (slug: string) => ['workspaces', 'detail', slug] as const,
  members: (slug: string) => ['workspaces', slug, 'members'] as const,
  statuses: (slug: string) => ['workspaces', slug, 'statuses'] as const,
};

export const workspacesQueryOptions = (api: ApiClient) =>
  queryOptions({
    queryKey: workspaceKeys.all,
    // Workspace list is essentially static within a session — only
    // changes when the user joins / leaves a workspace, which is
    // explicitly invalidated on the relevant mutations. Cache
    // aggressively so sidebar switcher stays instant.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces');
      if (error) throw error;
      return data;
    },
  });

export const useWorkspaces = () => {
  const api = useApi();
  return useQuery(workspacesQueryOptions(api));
};

export const workspaceQueryOptions = (api: ApiClient, slug: string) =>
  queryOptions({
    queryKey: workspaceKeys.detail(slug),
    // Name + slug rarely change. Renames are rare; settings pages
    // explicitly invalidate this key after a write.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{slug}', {
        params: { path: { slug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useWorkspace = (slug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...workspaceQueryOptions(api, slug ?? ''),
    enabled: Boolean(slug),
  });
};

export const workspaceMembersQueryOptions = (api: ApiClient, slug: string) =>
  queryOptions({
    queryKey: workspaceKeys.members(slug),
    // Member list churns slowly. People page mutations invalidate
    // this key explicitly; the mention picker and assignee select
    // benefit from instant reads on every render.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/members', {
        params: { path: { workspace_slug: slug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useWorkspaceMembers = (slug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...workspaceMembersQueryOptions(api, slug ?? ''),
    enabled: Boolean(slug),
  });
};

export const workspaceStatusesQueryOptions = (api: ApiClient, slug: string) =>
  queryOptions({
    queryKey: workspaceKeys.statuses(slug),
    staleTime: 5 * 60_000, // statuses change rarely; cache aggressively
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/statuses', {
        params: { path: { workspace_slug: slug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useWorkspaceStatuses = (slug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...workspaceStatusesQueryOptions(api, slug ?? ''),
    enabled: Boolean(slug),
  });
};

export type StatusCategory = 'todo' | 'in_progress' | 'complete';

export interface CreateTaskStatusInput {
  name: string;
  category: StatusCategory;
  color: string;
  is_default?: boolean;
}

export const useCreateTaskStatus = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTaskStatusInput) => {
      const { data, error } = await api.POST('/v1/workspaces/{workspace_slug}/statuses', {
        params: { path: { workspace_slug: workspaceSlug } },
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: workspaceKeys.statuses(workspaceSlug) });
    },
  });
};

export interface UpdateTaskStatusInput {
  id: string;
  name?: string;
  color?: string;
  is_default?: boolean;
}

export const useUpdateTaskStatus = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTaskStatusInput) => {
      const { id, ...body } = input;
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/statuses/{id}',
        {
          params: { path: { workspace_slug: workspaceSlug, id } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: workspaceKeys.statuses(workspaceSlug) });
      // Status changes can affect task lists' rendering (color/name on cards)
      // — invalidate broadly so the board / list view repaint.
      await qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] });
    },
  });
};

interface StatusListAttrs {
  category?: StatusCategory;
  position?: number;
  archived_at?: string | null;
  [key: string]: unknown;
}

interface StatusListItem {
  id: string;
  attributes?: StatusListAttrs;
}

interface StatusListPayload {
  data?: StatusListItem[];
  [key: string]: unknown;
}

export interface ReorderTaskStatusInput {
  /** ULID of the status being moved. Carried in the mutate input (not the
   *  hook factory) so the closure doesn't depend on a render commit —
   *  same pattern as `useReorderTask`. */
  id: string;
  /** 1-indexed target position within the status's category. */
  position: number;
}

/**
 * Optimistic mirror of the server's renumberSiblings logic, scoped to
 * (workspace, category). Pure function — never mutates input.
 */
const applyStatusReorderToCache = (
  list: StatusListPayload | undefined,
  statusId: string,
  rawTargetPosition: number,
): StatusListPayload | undefined => {
  if (!list?.data) return list;
  const moved = list.data.find((s) => s.id === statusId);
  if (!moved) return list;

  const category = moved.attributes?.category;
  if (!category) return list;

  const siblings = list.data.filter(
    (s) =>
      s.id !== statusId &&
      s.attributes?.category === category &&
      !s.attributes?.archived_at,
  );
  siblings.sort((a, b) => {
    const pa = a.attributes?.position ?? 0;
    const pb = b.attributes?.position ?? 0;
    if (pa !== pb) return pa - pb;
    return a.id.localeCompare(b.id);
  });

  const clamped = Math.max(1, Math.min(rawTargetPosition, siblings.length + 1));

  const ordered: StatusListItem[] = [];
  let cursor = 1;
  let inserted = false;
  for (const s of siblings) {
    if (!inserted && cursor === clamped) {
      ordered.push({ ...moved, attributes: { ...(moved.attributes ?? {}), position: cursor } });
      inserted = true;
      cursor++;
    }
    ordered.push({ ...s, attributes: { ...(s.attributes ?? {}), position: cursor } });
    cursor++;
  }
  if (!inserted) {
    ordered.push({ ...moved, attributes: { ...(moved.attributes ?? {}), position: cursor } });
  }

  const renumbered = new Map(ordered.map((s) => [s.id, s]));
  const next = list.data.map((row) => renumbered.get(row.id) ?? row);
  return { ...list, data: next };
};

export const useReorderTaskStatus = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReorderTaskStatusInput) => {
      const { id, position } = input;
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/statuses/{id}/reorder',
        {
          params: { path: { workspace_slug: workspaceSlug, id } },
          body: { position },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (input) => {
      const key = workspaceKeys.statuses(workspaceSlug);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<StatusListPayload>(key);
      qc.setQueryData<StatusListPayload>(key, (old) =>
        applyStatusReorderToCache(old, input.id, input.position),
      );
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(workspaceKeys.statuses(workspaceSlug), ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: workspaceKeys.statuses(workspaceSlug) });
    },
  });
};

export const useArchiveTaskStatus = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE('/v1/workspaces/{workspace_slug}/statuses/{id}', {
        params: { path: { workspace_slug: workspaceSlug, id } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: workspaceKeys.statuses(workspaceSlug) });
      // Archive may have repointed tasks to a different status — invalidate task lists.
      await qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] });
    },
  });
};

export interface CreateWorkspaceInput {
  name: string;
}

export const useCreateWorkspace = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWorkspaceInput) => {
      const { data, error } = await api.POST('/v1/workspaces', { body: input });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
};

export interface UpdateWorkspaceInput {
  name?: string;
  /**
   * Short uppercase task identifier prefix (slice 10). 2-10 chars
   * matching `[A-Z][A-Z0-9]{1,9}`. Renaming reflows every rendered
   * task code (e.g. `OLD-42` → `FLUX-42`); the SPA confirms before
   * sending. Sequence numbers stay stable.
   */
  task_prefix?: string;
  /**
   * Slice 12 — controls whether the PR-body bot comment is posted on
   * linked PRs. Defaults `true` server-side. Disabling silences only
   * the public PR comment; the in-app linking surface keeps working.
   */
  github_bot_comment_enabled?: boolean;
}

/**
 * Update a workspace's name and/or `task_prefix`. Workspace `admin`
 * or `owner` role required (server-enforced). On success, every
 * cached `task.code` value re-renders on the next read because the
 * code is a server-computed field on `TaskResource` — but we still
 * invalidate the workspace + tasks caches to force a refetch so
 * the change shows immediately, not on next focus.
 */
export const useUpdateWorkspace = (slug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateWorkspaceInput) => {
      const { data, error } = await api.PATCH('/v1/workspaces/{slug}', {
        params: { path: { slug } },
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: workspaceKeys.detail(slug) }),
        qc.invalidateQueries({ queryKey: workspaceKeys.all }),
        // task_prefix change re-renders every task code; invalidate
        // every task family for this workspace.
        qc.invalidateQueries({ queryKey: ['tasks', 'project', slug] }),
        qc.invalidateQueries({ queryKey: ['tasks', 'workspace', slug] }),
        qc.invalidateQueries({ queryKey: ['tasks', 'detail', slug] }),
      ]);
    },
  });
};

/**
 * Upload a workspace logo. Multipart so we bypass openapi-fetch's
 * default JSON serialiser via `bodySerializer: (b) => b` (which
 * returns the FormData verbatim — `fetch` then sets the right
 * `Content-Type: multipart/form-data; boundary=…` header itself).
 *
 * Workspace `admin` / `owner` only — server-enforced. On success
 * we invalidate both the workspace detail (the new `image_url`
 * shows up wherever `useWorkspace(slug)` is read) and the
 * workspaces list (sidebar switcher dropdown re-renders with the
 * fresh icon for this row).
 */
export const useUploadWorkspaceImage = (slug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('image', file);
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/image',
        {
          params: { path: { workspace_slug: slug } },
          // openapi-fetch types the multipart body as `unknown` —
          // cast to `never` so the call typechecks without us
          // having to widen the generated schema.
          body: undefined as never,
          bodySerializer: () => formData,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: workspaceKeys.detail(slug) }),
        qc.invalidateQueries({ queryKey: workspaceKeys.all }),
      ]);
    },
  });
};

/**
 * Remove the workspace's custom logo. Server-side delete is
 * idempotent (no-op when none was set), so we don't need a
 * `image_url` precondition on the call site — toggling the
 * affordance off is enough.
 */
export const useDeleteWorkspaceImage = (slug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/image',
        { params: { path: { workspace_slug: slug } } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: workspaceKeys.detail(slug) }),
        qc.invalidateQueries({ queryKey: workspaceKeys.all }),
      ]);
    },
  });
};
